import { computed, ref, shallowRef, watch } from "vue";
import { useAsyncData } from "./useAsyncData";
import { getCitiesV2, getMbCities } from '../assets/js/utils';
import { toValue } from "../assets/js/vue/utlis/toValue";
import { EMBProductCode } from "../components-manual/widgets/MeetingForm/const";

/**
 * @enum {string}
 */
export const ERepositoryCities = {
    MB: 'mb',
    NETWORK: 'network',
};

/**
 * @typedef ListState
 * @type {object}
 * @property {import('vue').Ref<object>} valueObject - элемент из списка, соответствующий value
 * @property {import('vue').Ref<string>} productId - ид продукта из mb
 */

/**
 * Получает опции для селекта (все параметры реактивные)
 * @param {string} filterRegionCode код субъекта РФ (только для repository === mb)
 * @param {EMBProductCode} filterProductCode код продукта
 * @param {ERepositoryCities} repository от куда брать список городов
 * @param {number|string} value - значение code для определения valueObject
 * @param {object} options options
 * @param {boolean} options.immediate - опция аналогично useAsyncData
 * @param {boolean} options.disabled - отключить отслеживание параметров и запрос списка
 * @returns {ListState}
 */
export const useBankCityList = (filterRegionCode, filterProductCode, repository, value, options = {}) => {
    const immediate = !toValue(options?.disabled) && options?.immediate || false;
    const productId = ref('');

    const citiesQuery = computed(() => {
        const query = {};

        if (toValue(repository) === ERepositoryCities.MB) {
            query.regionCode = toValue(filterRegionCode);
            query.productCode = toValue(filterProductCode);
        } else if (toValue(filterProductCode) === EMBProductCode.ESCROW) {
            if (!query.where) query.where = {};
            query.where['offices.isEscrow'] = 'y';
        }
        return query;
    });

    const watched = () => toValue(options?.disabled) ? null : citiesQuery.value;

    const { pending, data, execute, error } = useAsyncData(async ({ signal }) => {
        let citiesList = [];

        if (toValue(options?.disabled)) return citiesList;

        if (toValue(repository) === ERepositoryCities.MB) {
            const { cities, productCode } = await getMbCities(citiesQuery.value, 'city', { signal });

            productId.value = productCode;

            citiesList = cities?.map((city) => ({
                const: city.city,
                title: city.city,
                raw: city,
            })) ?? [];
        } else {
            const { data: { data: cities } } = await getCitiesV2(citiesQuery.value, 'name', { signal });

            citiesList = cities?.map((city) => ({
                const: `${city.id}`,
                title: city.attributes.name,
                raw: city,
            })) ?? [];
        }

        return citiesList;
    }, { deep: false, watch: watched, default: () => [], immediate });

    /** объект с выбранным значением (по const итема из списка) value */
    const valueObject = shallowRef(null);

    watch([data, () => toValue(value)], ([data, value]) => {
        if (!value || data.length === 0) {
            valueObject.value = null;
            return;
        }
        valueObject.value = data.find((item) => item.const === value) ?? null;
    });

    return {
        data,
        pending,
        execute,
        error,
        valueObject,
        productId,
    };
};
